import type { DocumentWithTimestamps, Timestamp } from './firebase.ts'
import type { Language } from './Language.ts'
import type { UserPrivateProfile, UserProfile } from './User.ts'

export enum ELinkedInConnectionStage {
    PUBLIC_PROFILE = 'PUBLIC_PROFILE',
    TALENTBASE_PROFILE = 'TALENTBASE_PROFILE',
    PRIVATE_PROFILE = 'PRIVATE_PROFILE'
}

export type TLinkedinConnection = DocumentWithTimestamps & {
    id: string
    userId: string
    domains: EMemberSnapshotDataDomains[]
    lastData?: TLinkedInMapperData
    lastDataAt?: Timestamp
    syncStages: ELinkedInConnectionStage[]
    syncFields: string[]
    tokenType: 'Bearer'
    accessToken: string
    expiresAt: Timestamp
    expiresIn: number
}

export type TLinkedInOrganization = {
    name: string
    position?: string
    description?: string
    startDate?: Date
    endDate?: Date
}

export type TLinkedInPhoneNumber = {
    number: string
    extension?: string
    type?: string
}

export type TLinkedInRichMediaItem = {
    link: string
    description?: string
    timestamp?: Date
}

export type TLinkedInPublicProfile = Partial<UserProfile> & {
    fullName?: string
    headline?: string
    summary?: string
    industry?: string
    location?: string
    organizations?: TLinkedInOrganization[]
    languages?: Language[]
}

export type TLinkedInPrivateProfile = Partial<UserPrivateProfile> & {
    websites?: string[]
    address?: string
    birthDate?: Date
    zipCode?: string
    phoneNumbers?: TLinkedInPhoneNumber[]
    richMedia?: TLinkedInRichMediaItem[]
}

export type TLinkedInMapperData = {
    profile: {
        public: TLinkedInPublicProfile
        private: TLinkedInPrivateProfile
    }
}

/**
 * Profile data response from LinkedIn API including fetched domains
 */
export type TLinkedInProfileDataWithDomains = TLinkedInMapperData['profile'] & {
    domains?: EMemberSnapshotDataDomains[]
}

export type TMemberUserInfoResponse = {
    name: string
    locale: {
        country: string
        language: string
    }
    given_name: string
    family_name: string
    picture?: string
}

export type TMemberSnapshotResponse =
    | {
          paging: {
              start: number
              count: number
              links: {
                  type: string
                  rel: 'prev' | 'next'
                  href: string
              }[]
              total: number
          }
          elements: TMemberSnapshotDomains[]
      }
    | {
          message: string
          status: number
      }

/**
 * @see https://learn.microsoft.com/en-us/linkedin/dma/member-data-portability/shared/snapshot-domain?view=li-dma-data-portability-2024-08
 */
export enum EMemberSnapshotDataDomains {
    /**
     * The basic biographical information that makes up the member's LinkedIn profile.
     */
    PROFILE = 'PROFILE',
    /**
     * Includes URL links to any photos, videos, or documents shared on LinkedIn.
     */
    RICH_MEDIA = 'RICH_MEDIA',
    /**
     * Phone numbers associated with the member's LinkedIn account.
     */
    PHONE_NUMBERS = 'PHONE_NUMBERS',
    /**
     * Job roles listed on the member's profile, including company names, titles, descriptions, locations, and dates.
     */
    POSITIONS = 'POSITIONS',
    /**
     * Organizations listed on the member's profile.
     */
    ORGANIZATIONS = 'ORGANIZATIONS',
    /**
     * Schools listed on the member's profile, including dates attended, degrees earned, and activities participated in.
     */
    EDUCATION = 'EDUCATION',
    /**
     * Projects listed on the member's profile.
     */
    PROJECTS = 'PROJECTS',
    /**
     * Publications listed on the member's profile.
     */
    PUBLICATIONS = 'PUBLICATIONS',
    /**
     * Honors listed on the member's profile.
     */
    HONORS = 'HONORS',
    /**
     * Languages listed by the member along with their level of proficiency.
     */
    LANGUAGES = 'LANGUAGES'
    /**
     * Date and time your LinkedIn account was created, closed, or reopened.
     */
    // ACCOUNT_HISTORY = 'ACCOUNT_HISTORY',
    /**
     * List of the ads you've clicked on.
     */
    // ADS_CLICKED = 'ADS_CLICKED',
    /**
     * A list of people you follow on LinkedIn.
     */
    // MEMBER_FOLLOWING = 'MEMBER_FOLLOWING',
    /**
     * Shows all the stored account logins for your account.
     */
    // LOGIN = 'LOGIN',
    /**
     * A list of your recent searches on LinkedIn.
     */
    // SEARCHES = 'SEARCHES',
    /**
     * Key inferences made about you based on your profile and activity on LinkedIn.
     */
    // INFERENCE_TAKEOUT = 'INFERENCE_TAKEOUT',
    /**
     * A list of the hashtags you follow on LinkedIn, along with created and last modified date.
     */
    // MEMBER_HASHTAG = 'MEMBER_HASHTAG',
    /**
     * Comments that you've made, excluding comments on posts in Groups.
     */
    // ALL_COMMENTS = 'ALL_COMMENTS',
    /**
     * Contains the updates you "Liked" except in Groups.
     */
    // ALL_LIKES = 'ALL_LIKES',
    /**
     * Posts started in groups by the member.
     */
    // GROUP_POSTS = 'GROUP_POSTS',
    /**
     * The current name on the member's account and any previous name changes.
     */
    // NAME_CHANGES = 'NAME_CHANGES',
    /**
     * Contacts imported on LinkedIn.
     */
    // CONTACTS = 'CONTACTS',
    /**
     * Events attended, been invited to but declined, or have requested to attend by the member.
     */
    // EVENTS = 'EVENTS',
    /**
     * Details of purchases related to the member's LinkedIn account.
     */
    // RECEIPTS = 'RECEIPTS',
    /**
     * Contains information LinkedIn uses to determine what ads to show you.
     */
    // AD_TARGETING = 'AD_TARGETING',
    /**
     * The date the member registered on LinkedIn.
     */
    // REGISTRATION = 'REGISTRATION',
    /**
     * Ratings and reviews provided by the member, including product reviews, service provider reviews, and LinkedIn Learning reviews.
     */
    // REVIEWS = 'REVIEWS',
    /**
     * List of searched people, along with the date of the search.
     */
    // SAVED_PEOPLE_SEARCHES = 'SAVED_PEOPLE_SEARCHES',
    /**
     * Articles authored by the member.
     */
    // ARTICLES = 'ARTICLES',
    /**
     * Information about any patents held by the member listed on their LinkedIn profile.
     */
    // PATENTS = 'PATENTS',
    /**
     * LinkedIn groups the member is a part of.
     */
    // GROUPS = 'GROUPS',
    /**
     * A list of the entities (e.g., companies) the member follows on LinkedIn.
     */
    // COMPANY_FOLLOWS = 'COMPANY_FOLLOWS',
    /**
     * Invitations sent and received by the member.
     */
    // INVITATIONS = 'INVITATIONS',
    /**
     * Name, position, company, and connection date of 1st degree connections of the member.
     */
    // CONNECTIONS = 'CONNECTIONS',
    /**
     * History of email addresses currently or previously associated with the member's account.
     */
    // EMAIL_ADDRESSES = 'EMAIL_ADDRESSES',
    /**
     * Jobs posted by the user.
     */
    // JOB_POSTINGS = 'JOB_POSTINGS',
    /**
     * Jobs applied by the user in the past.
     */
    // JOB_APPLICATIONS = 'JOB_APPLICATIONS',
    /**
     * Includes preferred job types, locations, industries, company size, dream companies, job titles, and activity level.
     */
    // JOB_SEEKER_PREFERENCES = 'JOB_SEEKER_PREFERENCES',
    /**
     * LinkedIn Learning videos watched by the member.
     */
    // LEARNING = 'LEARNING',
    /**
     * Messages sent and received on the member's inbox.
     */
    // INBOX = 'INBOX',
    /**
     * Contains information about the jobs that are saved for future reference.
     */
    // SAVED_JOBS = 'SAVED_JOBS',
    /**
     * Member's job alerts including saved date and job alerts URL.
     */
    // SAVED_JOB_ALERTS = 'SAVED_JOB_ALERTS',
    /**
     * Skills added to the member's profile.
     */
    // SKILLS = 'SKILLS',
    /**
     * Test scores listed on the member's profile.
     */
    // TEST_SCORES = 'TEST_SCORES',
    /**
     * Causes included on the member's profile.
     */
    // CAUSES_YOU_CARE_ABOUT = 'CAUSES_YOU_CARE_ABOUT',
    /**
     * Courses listed on the member's profile.
     */
    // COURSES = 'COURSES',
    /**
     * Contains a list of certifications included in the member's profile.
     */
    // CERTIFICATIONS = 'CERTIFICATIONS',
    /**
     * Mobile app calendar that has been synced with LinkedIn. Includes calendar source, owner, and last synced date.
     */
    // CALENDAR = 'CALENDAR',
    /**
     * A list of recommendations received and given by the member.
     */
    // RECOMMENDATIONS = 'RECOMMENDATIONS',
    /**
     * A list of everyone who's endorsed the member.
     */
    // ENDORSEMENTS = 'ENDORSEMENTS',
    /**
     * Contains volunteering experience. This includes organization names, roles, causes, start and end dates, and descriptions.
     */
    // VOLUNTEERING = 'VOLUNTEERING',
    /**
     * Contains all shared or re-shared posts. Includes date, URL, shared comments, and visibility status.
     */
    // SHARES = 'SHARES'
}

export type TMemberSnapshotDomains =
    | TSnapshotProfile
    | TSnapshotRichMedia
    | TSnapshotPhoneNumbers
    | TSnapshotPositions
    | TSnapshotOrganizations
    | TSnapshotEducation
    | TSnapshotPublications
    | TSnapshotProjects
    | TSnapshotHonors
    | TSnapshotLanguages

/**
 * For the `PROFILE` domain
 */
export type TSnapshotProfile = {
    snapshotDomain: EMemberSnapshotDataDomains.PROFILE
    snapshotData: {
        Websites: string
        Address: string
        'Maiden Name': string
        'Instant Messengers': string
        'First Name': string
        'Geo Location': string
        'Twitter Handles': string
        Industry: string
        'Zip Code': string
        Headline: string
        Summary: string
        'Birth Date': string
        'Last Name': string
    }[]
}

/**
 * For the `RICH_MEDIA` domain
 */
export type TSnapshotRichMedia = {
    snapshotDomain: EMemberSnapshotDataDomains.RICH_MEDIA
    snapshotData: {
        'Media Link': string
        'Media Description': string
        'Date/Time': string
    }[]
}

/**
 * For the `PHONE_NUMBERS` domain
 */
export type TSnapshotPhoneNumbers = {
    snapshotDomain: EMemberSnapshotDataDomains.PHONE_NUMBERS
    snapshotData: {
        Number: string
        Extension: string
        Type: string
    }[]
}

/**
 * For the `POSITIONS` domain
 */
export type TSnapshotPositions = {
    snapshotDomain: EMemberSnapshotDataDomains.POSITIONS
    snapshotData: {
        'Finished On': string
        Title: string
        'Company Name': string
        'Description': string
        Location: string
        'Started On': string
    }[]
}

/**
 * For the `EDUCATION` domain
 */
export type TSnapshotEducation = {
    snapshotDomain: EMemberSnapshotDataDomains.EDUCATION
    snapshotData: {
        Activities: string
        'Degree Name': string
        'Start Date': string
        'School Name': string
        'End Date': string
        Notes: string
    }[]
}

/**
 * For the `PUBLICATIONS` domain
 */
export type TSnapshotPublications = {
    snapshotDomain: EMemberSnapshotDataDomains.PUBLICATIONS
    snapshotData: {
        Publisher: string
        Description: string
        'Published On': string
        Url: string
        Name: string
    }[]
}

/**
 * For the `PROJECTS` domain
 */
export type TSnapshotProjects = {
    snapshotDomain: EMemberSnapshotDataDomains.PROJECTS
    snapshotData: {
        'Finished On': string
        Title: string
        Description: string
        Url: string
        'Started On': string
    }[]
}

/**
 * For the `HONORS` domain
 */
export type TSnapshotHonors = {
    snapshotDomain: EMemberSnapshotDataDomains.HONORS
    snapshotData: {
        Title: string
        'Issued On': string
        Description: string
    }[]
}

/**
 * For the `ORGANIZATIONS` domain
 */
export type TSnapshotOrganizations = {
    snapshotDomain: EMemberSnapshotDataDomains.ORGANIZATIONS
    snapshotData: {
        Name: string
        Position: string
        Description: string
        'Started On': string
        'Finished On': string
    }[]
}

/**
 * For the `LANGUAGES` domain
 */
export type TSnapshotLanguages = {
    snapshotDomain: EMemberSnapshotDataDomains.LANGUAGES
    snapshotData: {
        Name: string
        Proficiency: string
    }[]
}
